import { CSSProperties, cx } from '@linaria/core';
import Icon from '@mdi/react';
import { useEffect, useMemo, useState } from 'react';
import { User } from '../../../../types';
import { locales } from '../../../locales/en';
import { markAlertShown } from '../../../state/state.alerts';
import IconButton from '../../Buttons/IconButton';
import { BadgedIcon } from '../../common/CountBadge';
import ModelStatusBadge from '../../common/ModelStatusBadge';
import Popover from '../../common/Popover';
import { useAlerts, useHeaderAlertsFilter } from '../../Hooks/useAlerts';
import Alerts from '../Friends/Alerts';
import "./HeaderAlerts.linaria.module.css!=!./HeaderAlerts?./HeaderAlerts.linaria.module.css?css=H4sIAAAAAAAAA51TTY+jOBC9z6/gslKyarcwSTpp5rKnkea0K+0PsApcgMHYbmM+Emv++yohEEjPHGbhgJ7rvapHueoVXM3KhOVDeDmEvhaK9IK7Io7eQjN8/fHl9UqQmmUuFzJaI2+Ac6Fy4rSJTws6ruj4TE+0c7peKt5L5mSFofRpaxttY6OFcmineLljYnfC1PoahrvDWqjN7uryJUhBphsahn8EJNhFZthuJ2FdsSKq6/LNG90IJ7SKLUpwosO5dsOoG3rRSc9FYySc40zibA0s62yX4rBG/sohXFhMx6y6nxQJMvrxnqb55QmOmt6CiZW+fiZFwWjxpsv2skYepMgVEQ7rJk5x2RB5YoKbLN9PTY3DKbTLplCiLUf7iESc0TI6lUP0BH0CaZVb3SpOUi21jZ0F1RiwqNykPkpGq/6o6r0fOUIVaMUcT7o5nmnlnsMYMaoOsstaX6DICxd3YDeENFI7Mp7Mt9adGU32u91g/XjXC+rtYGZCzag59+Igb82Nw4AG0Do9D04yNeMx2nM/2pRROmD3Ufrb/47zcSMF0T6smwChuc7JXzVyAcHGWMzQNsQib1PkpNY3RTDirb9lPVykPXYsl+c+qz+fLEsprfDrj7sbw6g7h7Z0HpSo4UYoEDjab8DxuwqicLJEdOt+1xZGhup+aWt1sii6cgX1RLj1d4yNofzC6D6BCC5P0Ndgc6GIxMzFt90kNPgzuF+ig7QiukMrwTwWtf9gh75IytxfiFAch3hJv8w8w+hb8U5P5acu/aPNz5o0eRvHYI1WRsNHiepwgDz7VOHfFCT+3bogiuYiQgWZtj1Y3vz2oNisssXS2OrEawOpcOersWkFQ8YrYY7cJ3ogTQFc93EYXF/6ejDDvcWPbd4uV78S0NLLE/y8+r/OkTNa0Cirjk9wmUPUkOM9B3TgwJIMpLwySGvl9kUKhWBJboELVG7zJVg8P9ct0ucWz9uX/6GZJdff+Q/14nH+9gYAAA=="
const notificationsSvg = `M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2m6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1z`;
const STACK_SIZE = 3;
const STACK_AVATAR = 16;
const STACK_AVATAR_DESKTOP = 24;
const STACK_OVERLAP_RATIO = 0.4;
const BELL_SLOT = 22;
// the bell fills more of its box than the envelope, so this matches their glyph heights
const BELL_SLOT_COMPACT = 19.2;
const STACK_ITEM_MS = 20 * 1000;
const LEAVE_MS = 220;
const alertStyles = "atm_jb_gx0z50 atm_lo_ftgil2 atm_le_ftgil2";
const pointerStyles = "atm_9j_tlke0l";
const stylesPopOver = "atm_j3_i38ecr";
const slotStyles = "atm_mk_h2mmj6 atm_9s_1txwivl atm_ar_vrvcex atm_be_1q9ccgz atm_h_1h6ojuz atm_l8_idpfg4 atm_3f_idpfg4 atm_2d_1j28jx2 atm_7l_1kw7nm4 atm_bv_1kw7nm4 atm_e2_1n5lvfu atm_vy_1b433xr atm_am_1pywi5l atm_jb_idpfg4 atm_uc_11xevqj atm_9j_tlke0l atm_5zlr7v_glywfm";
const bellFadeInStyles = "atm_p_1ty0rjt atm_e2p1ow_glywfm";
const stackedItemStyles = "atm_mk_h2mmj6 atm_9s_1txwivl atm_h_1h6ojuz atm_am_glywfm atm_gz_14ba2az atm_wq_5whbjg atm_p_16h918j atm_e2p1ow_glywfm";
const firstItemStyles = "atm_gz_idpfg4";
const outgoingItemStyles = "atm_p_k55agf atm_e2p1ow_glywfm atm_5rfkrh_idpfg4";

// stacked discs overlap, so the translucent fallback needs an opaque base
const avatarStyles = "atm_70_dkip7d atm_2d_1kiau1z atm_2g_1h12fk7";
interface StackItem {
  id: number;
  kind: keyof User;
  news: boolean;
  leaving: boolean;
  expiresAt: number;
}
interface StackAvatarProps {
  index: number;
  item: StackItem;
  size: number;
}
const StackAvatar = ({
  index,
  item,
  size
}: StackAvatarProps) => {
  const {
    id,
    kind,
    news,
    leaving
  } = item;
  return <div className={cx(stackedItemStyles, index === 0 && firstItemStyles, leaving && outgoingItemStyles)} style={{
    '--stack-z': STACK_SIZE - index
  } as CSSProperties}>
            <ModelStatusBadge id={id} size={size} kind={kind} news={news} disableDrag avatarClassName={avatarStyles} />
        </div>;
};
interface HeaderAlertsProps {
  compactHeader: boolean;
  stack?: boolean;
  stackOnly?: boolean;
}
const HeaderAlerts = ({
  compactHeader,
  stack,
  stackOnly
}: HeaderAlertsProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);
  const [showAlerts, setShowAlerts] = useState(false);
  const {
    alerts,
    unread,
    resetUnread,
    clearAlerts
  } = useAlerts({
    unreadCount: true
  });
  const allowsAlert = useHeaderAlertsFilter();
  const unreadAlerts = useMemo(() => alerts.reduce((count, alert) => alert.unread && allowsAlert(alert) ? count + 1 : count, 0), [alerts, allowsAlert]);
  const [clock, setClock] = useState(Date.now);
  const [mountedAt] = useState(Date.now);
  const stackItems = useMemo(() => {
    if (!stack || unread === 0) return [] as StackItem[];
    const now = alerts.reduce((latest, alert) => Math.max(latest, alert.receivedAt ?? 0), clock);
    const items: StackItem[] = [];
    for (const alert of alerts) {
      if (alert.key === 'tokens' || !alert.unread) continue;
      if (alert.shownAt && alert.shownAt < mountedAt) continue;
      if (!allowsAlert(alert)) continue;
      if (items.some(item => item.id === alert.id)) continue;
      const receivedAt = alert.receivedAt ?? 0;
      const age = now - receivedAt;
      if (age >= STACK_ITEM_MS) break;
      items.push({
        id: alert.id,
        kind: alert.key,
        news: !!alert.news,
        leaving: age >= STACK_ITEM_MS - LEAVE_MS,
        expiresAt: receivedAt + STACK_ITEM_MS
      });
      if (items.length === STACK_SIZE) break;
    }
    return items;
  }, [stack, unread, alerts, clock, allowsAlert, mountedAt]);
  const nextTick = stackItems.reduce((soonest, item) => Math.min(soonest, item.leaving ? item.expiresAt : item.expiresAt - LEAVE_MS), Infinity);

  // re-arms on clock too, so a timer that fires early simply waits again
  useEffect(() => {
    if (nextTick === Infinity) return;
    const timeout = setTimeout(() => setClock(Date.now()), Math.max(nextTick - Date.now(), 0));
    return () => clearTimeout(timeout);
  }, [nextTick, clock]);

  // without an observer, show everything rather than nothing
  const [slotWidth, setSlotWidth] = useState(() => typeof ResizeObserver === 'undefined' ? Infinity : 0);
  useEffect(() => {
    if (!stack || !anchorEl || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      setSlotWidth(entry.contentRect.width);
    });
    observer.observe(anchorEl);
    return () => observer.disconnect();
  }, [stack, anchorEl]);
  const avatarSize = compactHeader ? STACK_AVATAR : STACK_AVATAR_DESKTOP;
  const bellSize = compactHeader ? BELL_SLOT_COMPACT : BELL_SLOT;
  const overlap = Math.round(avatarSize * STACK_OVERLAP_RATIO);
  const step = avatarSize - overlap;
  const capacity = slotWidth + 1 >= avatarSize ? 1 + Math.floor((slotWidth + 1 - avatarSize) / step) : 0;
  const visibleItems = stackItems.slice(0, capacity);
  useEffect(() => {
    visibleItems.forEach(item => markAlertShown(item.id));
  }, [stackItems, capacity]);
  const onToggleTooltip = () => {
    setShowAlerts(prev => !prev);
  };
  const onCloseAlerts = () => {
    setShowAlerts(false);
  };
  const popoverAlerts = alerts.slice(0, 6);
  const popover = <Popover anchorEl={anchorEl} open={showAlerts} onClose={onCloseAlerts} className={stylesPopOver}>
            <div className={alertStyles}>
                <Alerts alerts={popoverAlerts} clearAlerts={clearAlerts} embedded={true} onAlerts={resetUnread} onViewAllHistory={onCloseAlerts} />
            </div>
        </Popover>;
  if (stack) {
    // opening the popover resets unread, which collapses the stack at once
    const showStack = unreadAlerts > 0 && stackItems.length > 0;
    // stack-only keeps an empty anchor so an open popover survives the reset
    const showBell = !showStack && !stackOnly;
    // the observer reports what the header actually granted
    const requestedWidth = showStack ? avatarSize + (stackItems.length - 1) * step : showBell ? bellSize : 0;
    return <>
                <button type="button" ref={setAnchorEl} onClick={onToggleTooltip} className={slotStyles} style={{
        '--slot-width': `${requestedWidth}px`,
        '--slot-height': `${Math.max(bellSize, avatarSize)}px`,
        '--stack-overlap': `${overlap}px`
      } as CSSProperties} title={locales.header.recentNotifications} aria-expanded={showAlerts} aria-hidden={requestedWidth === 0} tabIndex={requestedWidth === 0 ? -1 : undefined} data-testid="HeaderAlerts__Slot">
                    {showStack && visibleItems.map((item, index) => <StackAvatar key={item.id} item={item} index={index} size={avatarSize} />)}
                    {showBell && <BadgedIcon circular count={unreadAlerts} size="medium" className={bellFadeInStyles}>
                            <Icon path={notificationsSvg} size={`${bellSize}px`} />
                        </BadgedIcon>}
                </button>
                {popover}
            </>;
  }
  return <>
            <IconButton allowOverflow ref={setAnchorEl} onClick={onToggleTooltip} style={compactHeader ? {
      paddingTop: 1,
      paddingBottom: 1,
      paddingLeft: 1,
      paddingRight: 1
    } : undefined} title={locales.header.recentNotifications}>
                <BadgedIcon circular count={unreadAlerts} size="medium">
                    <Icon path={notificationsSvg} size={compactHeader ? `${BELL_SLOT_COMPACT}px` : 1} className={pointerStyles} />
                </BadgedIcon>
            </IconButton>
            {popover}
        </>;
};
export default HeaderAlerts;